/**
 * California HCD Annual Progress Report — affordable housing tables (Supabase).
 * Loaded by python/build_affordable_housing_apr.py into db_affordable_housing_apr_*.
 */

import { supabaseClient } from './supabase';

/** Try common geofips string variants (DB may omit leading zeros). */
export function geofipsLookupVariants(geofips: number | null): string[] {
  if (geofips == null || Number.isNaN(geofips)) return [];
  const s = String(Math.trunc(Math.abs(geofips)));
  const out = new Set<string>();
  out.add(s);
  out.add(s.padStart(7, '0'));
  if (/^\d+$/.test(s)) {
    out.add(String(Number(s)));
    // CA place GEOIDs sometimes normalized to 11-digit Census-style in some tables
    out.add(s.padStart(11, '0'));
    const stripped = s.replace(/^0+/, '') || '0';
    out.add(stripped);
    out.add(stripped.padStart(7, '0'));
  }
  return Array.from(out);
}

async function fetchAprTableByGeofipsThenLocation<
  T extends Record<string, unknown>
>(
  table: string,
  columns: string,
  geofips: number | null,
  locationName: string | undefined | null,
  orderCol: { column: string; ascending: boolean }
): Promise<T[]> {
  const variants = geofipsLookupVariants(geofips);
  for (const gf of variants) {
    const { data, error } = await supabaseClient
      .from(table)
      .select(columns)
      .eq('geofips', gf)
      .order(orderCol.column, { ascending: orderCol.ascending });
    if (error) throw error;
    if (data?.length) return data as unknown as T[];
  }
  const name = locationName?.trim();
  if (name) {
    const { data: exact, error: err1 } = await supabaseClient
      .from(table)
      .select(columns)
      .eq('location_name', name)
      .order(orderCol.column, { ascending: orderCol.ascending });
    if (err1) throw err1;
    if (exact?.length) return exact as unknown as T[];
    const { data: loose, error: err2 } = await supabaseClient
      .from(table)
      .select(columns)
      .ilike('location_name', `%${name}%`)
      .order(orderCol.column, { ascending: orderCol.ascending });
    if (err2) throw err2;
    if (loose?.length) return loose as unknown as T[];
  }
  return [];
}

export type AffordableHousingAprSummaryRow = {
  geofips: string;
  location_name: string;
  geography_type: string;
  county: string | null;
  county_fips: string | null;
  state: string | null;
  year: number;
  jurisdiction_type: string | null;
  is_unincorporated_county_record: boolean | null;
  total_proposed_units: number | null;
  total_entitled_units: number | null;
  total_permitted_units: number | null;
  total_completed_units: number | null;
  completed_deed_restricted_affordable_units: number | null;
  completed_very_low_income_units: number | null;
  completed_low_income_units: number | null;
  completed_moderate_income_units: number | null;
  completed_above_moderate_units: number | null;
  completed_affordable_units_total: number | null;
  completed_affordable_share: number | null;
  completed_deed_restricted_share: number | null;
  preserved_affordable_units: number | null;
  total_affordable_impact_units: number | null;
  density_bonus_project_count: number | null;
  streamlined_project_count: number | null;
  infill_project_count: number | null;
  data_source: string | null;
  measurement: string | null;
  method_note: string | null;
};

export type AffordableHousingAprPipelineRow = {
  geofips: string;
  year: number;
  stage: string;
  income_level: string;
  deed_restricted: boolean;
  tenure: string;
  unit_category: string;
  units: number | null;
  project_count: number | null;
  source_table: string;
};

export type AffordableHousingAprPreservationRow = {
  geofips: string;
  year: number;
  activity_type: string;
  income_level: string;
  counts_toward_rhna: boolean;
  units: number | null;
};

export async function fetchAffordableHousingAprSummary(
  geofips: number | null,
  locationName?: string | null
): Promise<AffordableHousingAprSummaryRow[]> {
  return fetchAprTableByGeofipsThenLocation<AffordableHousingAprSummaryRow>(
    'db_affordable_housing_apr_summary',
    '*',
    geofips,
    locationName,
    { column: 'year', ascending: true }
  );
}

export async function fetchAffordableHousingAprPipeline(
  geofips: number | null,
  locationName?: string | null
): Promise<AffordableHousingAprPipelineRow[]> {
  return fetchAprTableByGeofipsThenLocation<AffordableHousingAprPipelineRow>(
    'db_affordable_housing_apr_pipeline',
    'geofips,year,stage,income_level,deed_restricted,tenure,unit_category,units,project_count,source_table',
    geofips,
    locationName,
    { column: 'year', ascending: true }
  );
}

export async function fetchAffordableHousingAprPreservation(
  geofips: number | null,
  locationName?: string | null
): Promise<AffordableHousingAprPreservationRow[]> {
  return fetchAprTableByGeofipsThenLocation<AffordableHousingAprPreservationRow>(
    'db_affordable_housing_apr_preservation',
    'geofips,year,activity_type,income_level,counts_toward_rhna,units',
    geofips,
    locationName,
    { column: 'year', ascending: true }
  );
}

export type AffordableHousingAprCapacityRow = {
  geofips: string;
  year: number;
  realistic_capacity: number | null;
};

export async function fetchAffordableHousingAprCapacity(
  geofips: number | null,
  locationName?: string | null
): Promise<AffordableHousingAprCapacityRow[]> {
  return fetchAprTableByGeofipsThenLocation<AffordableHousingAprCapacityRow>(
    'db_affordable_housing_apr_capacity',
    'geofips,year,realistic_capacity',
    geofips,
    locationName,
    { column: 'year', ascending: true }
  );
}
